import configDetails from '../Config/Config';
import React, { useState, useEffect } from 'react';

const ENTERPRISE_CONTACT_API_URL = `${configDetails.salesServiceBaseUrl}${configDetails.contactSalesEndpoint}`;

const ContactSalesModal = ({ isOpen, onClose, planName = 'Enterprise' }) => {
  const initialForm = {
    name: '',
    phone: '',
    email: '',
    company: '',
    country: '',
    company_website: '',
    country_code: '' ,
    remarks: ''
  };

  const [form, setForm] = useState(initialForm);
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [generalError, setGeneralError] = useState(null);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [countries] = useState([
    { name: 'United States', code: 'US', dialCode: '1' },
    { name: 'India', code: 'IN', dialCode: '91' },
    { name: 'United Kingdom', code: 'GB', dialCode: '44' },
    { name: 'Australia', code: 'AU', dialCode: '61' },
    { name: 'Germany', code: 'DE', dialCode: '49' },
    { name: 'France', code: 'FR', dialCode: '33' },
    { name: 'Singapore', code: 'SG', dialCode: '65' },
    { name: 'Japan', code: 'JP', dialCode: '81' },
    { name: 'Brazil', code: 'BR', dialCode: '55' }
  ]);
  const [dialCodes] = useState([
    { dialCode: '1', name: 'US/Canada' },
    { dialCode: '91', name: 'India' },
    { dialCode: '44', name: 'United Kingdom' },
    { dialCode: '61', name: 'Australia' },
    { dialCode: '49', name: 'Germany' },
    { dialCode: '33', name: 'France' },
    { dialCode: '65', name: 'Singapore' },
    { dialCode: '81', name: 'Japan' },
    { dialCode: '55', name: 'Brazil' }
  ]);
  const [isOtherCountry, setIsOtherCountry] = useState(false);
  const [isOtherDial, setIsOtherDial] = useState(false);

  const validate = () => {
    const errs = [];
    if (!form.name.trim()) errs.push({ field: 'name', message: 'Name is required' });
    if (!form.email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.push({ field: 'email', message: 'Valid email is required' });
    if (!form.phone.trim()) errs.push({ field: 'phone', message: 'Phone is required' });
    if (!form.country_code.trim()) errs.push({ field: 'country_code', message: 'Country code is required' });
    if (!form.company.trim()) errs.push({ field: 'company', message: 'Company is required' });
    if (!form.country.trim()) errs.push({ field: 'country', message: 'Country is required' });
    if (!form.remarks.trim()) errs.push({ field: 'remarks', message: 'Business requirements are required' });
    if (form.remarks && form.remarks.length > 1000) errs.push({ field: 'remarks', message: 'Business requirements must be 1000 characters or less' });
    return errs;
  };

  const handleChange = (field) => (e) => {
    let value = e.target.value;
    if (field === 'remarks') {
      value = value.slice(0, 1000);
    }
    if (field === 'phone') {
      // national number part only (digits)
      const digits = value.replace(/\D/g, '');
      setForm(prev => ({ ...prev, phone: digits }));
      // update display combining country code if available
      const code = prevOr(form.country_code, form.country_code) || '';
      if (digits) setPhoneDisplay(formatPhoneDisplay('+' + (code ? code : '') + digits));
      else setPhoneDisplay('');
      return;
    }
    if (field === 'country_code') {
      // allow + or digits, normalize to digits only for storage
      const digits = value.replace(/\D/g, '');
      setForm(prev => ({ ...prev, country_code: digits }));
      setIsOtherDial(true);
      if (form.phone) setPhoneDisplay(formatPhoneDisplay('+' + digits + form.phone));
      return;
    }
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleDialSelect = (e) => {
    const val = e.target.value;
    if (val === 'Other') {
      setIsOtherDial(true);
      setForm(prev => ({ ...prev, country_code: '' }));
    } else {
      setIsOtherDial(false);
      // val is the dialCode
      setForm(prev => ({ ...prev, country_code: val }));
      if (form.phone) setPhoneDisplay(formatPhoneDisplay('+' + val + form.phone));
    }
  };

  // small helper to avoid reading stale form in handler; returns first truthy
  const prevOr = (a, b) => a || b;

  // Format phone display based on country code prefixes (basic friendly formatting)
  const formatPhoneDisplay = (raw) => {
    if (!raw) return '';
    // normalize
    const onlyDigits = raw.replace(/[^0-9]/g, '');
    // detect country code
    if (raw.startsWith('+1') || onlyDigits.startsWith('1') && onlyDigits.length <= 11) {
      // US format +1 (AAA) BBB-CCCC
      const national = onlyDigits.startsWith('1') ? onlyDigits.slice(1) : onlyDigits;
      const part1 = national.slice(0, 3);
      const part2 = national.slice(3, 6);
      const part3 = national.slice(6, 10);
      let formatted = '+1';
      if (part1) formatted += ' (' + part1 + ')';
      if (part2) formatted += ' ' + part2;
      if (part3) formatted += '-' + part3;
      return formatted.trim();
    }

    if (raw.startsWith('+91') || onlyDigits.startsWith('91')) {
      // India: +91 12345 67890 (5-5)
      const national = onlyDigits.startsWith('91') ? onlyDigits.slice(2) : onlyDigits;
      const part1 = national.slice(0, 5);
      const part2 = national.slice(5, 10);
      let formatted = '+91';
      if (part1) formatted += ' ' + part1;
      if (part2) formatted += ' ' + part2;
      return formatted.trim();
    }

    if (raw.startsWith('+44') || onlyDigits.startsWith('44')) {
      // UK: +44 1234 567890 (4-6 heuristic)
      const national = onlyDigits.startsWith('44') ? onlyDigits.slice(2) : onlyDigits;
      const part1 = national.slice(0, 4);
      const part2 = national.slice(4, 10);
      let formatted = '+44';
      if (part1) formatted += ' ' + part1;
      if (part2) formatted += ' ' + part2;
      return formatted.trim();
    }

    // Default grouping: +CC AAA BBB CCC
    const matches = onlyDigits.match(/^(\d{1,3})(\d{1,3})(\d{1,3})(\d*)$/);
    if (matches) {
      const groups = matches.slice(1).filter(Boolean);
      return '+' + groups.join(' ').trim();
    }
    return raw;
  };
  const resetState = () => {
    setForm(initialForm);
    setPhoneDisplay('');
    setErrors([]);
    setGeneralError(null);
    setLoading(false);
    setSuccess(null);
    setAttemptedSubmit(false);
    setIsOtherDial(false);
    setIsOtherCountry(false);
  };

  const handleClose = () => {
    resetState();
    if (typeof onClose === 'function') onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setGeneralError(null);
    setAttemptedSubmit(true);
    const clientErrors = validate();
    if (clientErrors.length) {
      setErrors(clientErrors);
      return;
    }
    setLoading(true);
    try {
      // Build phone as +<country_code><national_number>
      const countryDigits = (form.country_code || '').replace(/\D/g, '');
      const nationalDigits = (form.phone || '').replace(/\D/g, '');
      const finalPhone = (countryDigits ? '+' + countryDigits : '+') + nationalDigits;

      const payload = {
        name: form.name.trim(),
        phone: finalPhone,
        email: form.email.trim(),
        company: form.company.trim(),
        country: form.country.trim(),
        remarks: form.remarks.trim(),
        company_website: form.company_website.trim()
      };

      const res = await fetch(ENTERPRISE_CONTACT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data && data.success) {
        // Friendly success message (no ref id)
        setSuccess({ message: 'We have received your request. Our team will contact you shortly.' });
        // Clear form so reopening doesn't show previous details
        setForm(initialForm);
        setAttemptedSubmit(false);
      } else {
        // Duplicate submission handling
        if (data && data.code === 'NF_SALES_2001') {
          setGeneralError('A request with these details is already in progress. Please wait for our sales team to contact you.');
        } 
        else if (data && data.code === 'NF_SALES_3001') {
          setGeneralError('Request rejected. Please contact support@noteforge.ai for assistance.');
        }
        else if (data && data.errors && Array.isArray(data.errors)) {
          setErrors(data.errors.map(err => ({ field: err.field || 'form', message: err.message || 'Invalid' })));
          setGeneralError(data.message || null);
        } else if (data && data.message) {
          setGeneralError(data.message);
        } else {
          setGeneralError('Submission failed. Please try again later.');
        }
      }
    } catch (err) {
      console.error('[ContactSales] Error submitting', err);
      setGeneralError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getFieldError = (field) => {
    const e = errors.find(x => x.field === field);
    return e ? e.message : null;
  };

  // Using a curated list of popular countries to avoid external API dependency.

  const handleCountrySelect = (e) => {
    const val = e.target.value;
    if (val === 'Other') {
      setIsOtherCountry(true);
      setForm(prev => ({ ...prev, country: '' }));
    } else {
      setIsOtherCountry(false);
      // Only set the country name. Country dial code is handled separately by the code selector.
      setForm(prev => ({ ...prev, country: val }));
    }
  };

  const isFieldInvalid = (field) => {
    const requiredFields = ['name','email','phone','company','country','country_code','remarks'];
    return !!getFieldError(field) || (attemptedSubmit && !form[field]?.trim() && requiredFields.includes(field));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose}></div>
      <div className="relative w-full max-w-2xl mx-2 sm:mx-4 bg-white rounded-xl sm:rounded-2xl shadow-2xl p-4 sm:p-8 z-10 my-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4 gap-2">
          <div className="flex-1">
            <h3 className="text-lg sm:text-xl font-bold text-gray-800">Contact Sales — {planName}</h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">Tell us about your needs and our team will get back to you.</p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-700 flex-shrink-0 mt-1">
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        {success ? (
          <div className="mt-6 text-center">
            <div className="text-green-600 font-semibold">We have received your request.</div>
            <div className="text-sm text-gray-600 mt-2">Our
                 team will contact you shortly.</div>
            <div className="mt-4">
              <button onClick={() => { handleClose(); }} className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl">Close</button>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-y-auto max-h-[calc(90vh-180px)]">
              <form onSubmit={handleSubmit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 pb-20">
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-gray-700">Full name <span className="text-red-600">*</span></label>
              <input value={form.name} onChange={handleChange('name')} className={`mt-1 w-full rounded-lg px-3 py-2 focus:outline-none focus:ring-2 ${isFieldInvalid('name') ? 'border border-red-400 ring-red-100' : 'border border-gray-200 ring-blue-200'}`} />
              {getFieldError('name') && <div className="text-red-600 text-sm mt-1">{getFieldError('name')}</div>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-1">
                <label className="text-sm font-medium text-gray-700">Country code <span className="text-red-600">*</span></label>
                <select value={isOtherDial ? 'Other' : (form.country_code || '')} onChange={handleDialSelect} className={`mt-1 w-full rounded-lg px-2 py-2 focus:outline-none focus:ring-2 ${isFieldInvalid('country_code') ? 'border border-red-400 ring-red-100' : 'border border-gray-200 ring-blue-200'}`}>
                  <option value="">Select code</option>
                  {dialCodes.map(d => (
                    <option key={d.dialCode} value={d.dialCode}>+{d.dialCode} {d.name}</option>
                  ))}
                  <option value="Other">Other</option>
                </select>
                {isOtherDial && (
                  <div className="mt-2 flex items-center border border-gray-200 rounded-lg overflow-hidden">
                    <span className="px-3 py-2 bg-gray-100 text-gray-600 font-medium">+</span>
                    <input value={form.country_code} onChange={handleChange('country_code')} placeholder="e.g. 91 or 44" className="flex-1 px-3 py-2 focus:outline-none focus:ring-2" />
                  </div>
                )}
                {getFieldError('country_code') && <div className="text-red-600 text-sm mt-1">{getFieldError('country_code')}</div>}
              </div>

              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-gray-700">Phone (number) <span className="text-red-600">*</span></label>
                <input value={form.phone} onChange={handleChange('phone')} className={`mt-1 w-full rounded-lg px-3 py-2 focus:outline-none focus:ring-2 ${isFieldInvalid('phone') ? 'border border-red-400 ring-red-100' : 'border border-gray-200 ring-blue-200'}`} placeholder="1234567890" />
                {getFieldError('phone') && <div className="text-red-600 text-sm mt-1">{getFieldError('phone')}</div>}
                {phoneDisplay && <div className="text-xs text-gray-500 mt-1">Preview: {phoneDisplay}</div>}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Email <span className="text-red-600">*</span></label>
              <input value={form.email} onChange={handleChange('email')} className={`mt-1 w-full rounded-lg px-3 py-2 focus:outline-none focus:ring-2 ${isFieldInvalid('email') ? 'border border-red-400 ring-red-100' : 'border border-gray-200 ring-blue-200'}`} placeholder="you@company.com" />
              {getFieldError('email') && <div className="text-red-600 text-sm mt-1">{getFieldError('email')}</div>}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Company <span className="text-red-600">*</span></label>
              <input value={form.company} onChange={handleChange('company')} className={`mt-1 w-full rounded-lg px-3 py-2 focus:outline-none focus:ring-2 ${isFieldInvalid('company') ? 'border border-red-400 ring-red-100' : 'border border-gray-200 ring-blue-200'}`} />
              {getFieldError('company') && <div className="text-red-600 text-sm mt-1">{getFieldError('company')}</div>}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Country <span className="text-red-600">*</span></label>
              <>
                <select value={isOtherCountry ? 'Other' : form.country} onChange={handleCountrySelect} className={`mt-1 w-full rounded-lg px-3 py-2 focus:outline-none focus:ring-2 ${isFieldInvalid('country') ? 'border border-red-400 ring-red-100' : 'border border-gray-200 ring-blue-200'}`}>
                  <option value="">Select a country</option>
                  {countries.map(c => (
                    <option key={c.code || c.name} value={c.name}>{c.name}</option>
                  ))}
                  <option value="Other">Other</option>
                </select>
                {isOtherCountry && (
                  <input value={form.country} onChange={handleChange('country')} placeholder="Enter country" className="mt-2 w-full rounded-lg px-3 py-2 border border-gray-200 focus:outline-none focus:ring-2" />
                )}
              </>
              {getFieldError('country') && <div className="text-red-600 text-sm mt-1">{getFieldError('country')}</div>}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Company Website (optional)</label>
              <input value={form.company_website} onChange={handleChange('company_website')} className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2" placeholder="https://yourcompany.com" />
            </div>

            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-gray-700">Business requirements (briefly describe your business needs) <span className="text-red-600">*</span></label>
              <textarea
                value={form.remarks}
                onChange={(e) => handleChange('remarks')( { target: { value: e.target.value.slice(0,1000) } } )}
                className={`mt-1 w-full rounded-lg px-3 py-2 h-28 focus:outline-none focus:ring-2 ${isFieldInvalid('remarks') ? 'border border-red-400 ring-red-100' : 'border border-gray-200 ring-blue-200'}`}
                maxLength={1000}
              />
              {getFieldError('remarks') && <div className="text-red-600 text-sm mt-1">{getFieldError('remarks')}</div>}
              <div className="text-xs text-gray-500 mt-1">{(form.remarks || '').length}/1000</div>
            </div>

            {generalError && <div className="sm:col-span-2 text-red-600 text-sm">{generalError}</div>}
            </form>
            </div>

            <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 pt-4 mt-4 flex items-center justify-end gap-3">
              <button type="button" onClick={handleClose} className="px-4 py-2 rounded-2xl border border-gray-200">Cancel</button>
              <button type="submit" disabled={loading} onClick={handleSubmit} className="px-4 py-2 rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 text-white disabled:opacity-60">
                {loading ? 'Submitting...' : 'Contact Sales'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ContactSalesModal;
